"use client";

import { Alert, Box, Button, Typography } from "@mui/material";
import { useState } from "react";
import { useAppTranslate } from "src/hooks/useAppTranslate";

/**
 * Tells the customer that photos from an interrupted upload were recovered —
 * and shows them WHICH.
 *
 * Chunked registration means a killed tab no longer loses everything: whatever
 * had landed is kept. But without this the rescue is invisible and actively
 * confusing. The customer returns, their cart holds more photos than they
 * remember adding, the cart shows only a group summary and one thumbnail, and
 * nothing explains where the rest came from. Observed on stage: an order held
 * 32 photos across three groups where the customer had personally added 9.
 *
 * Worse, the natural reaction — "only some are here, I'll just add them all
 * again" — silently doubles the rescued ones, because nothing dedupes on add.
 *
 * WHY THUMBNAILS AND NOT FILENAMES
 *
 * We cannot reliably say which of the customer's files are already here by
 * name. `photo` stores no byte size and no hash, and filenames repeat constantly
 * in this population — two different phones both produce image00004.jpeg, which
 * is the exact collision that used to overwrite photos in storage. Marking files
 * as "already uploaded" on a name match would confidently mislabel different
 * pictures, and a customer trusting that would lose them.
 *
 * So we show the actual images instead. The order response already signs every
 * photo's URL (PhotoSignInterceptor on the order endpoint), so this costs no new
 * request and no schema change. Identification becomes visual, which is the only
 * form of it we can honestly offer.
 *
 * WHY THE IMAGES ARE OPT-IN
 *
 * The order response signs the FULL-SIZE original -- there is no server-side
 * thumbnailing. A measured stage photo was 186 KB, and real customer photos run
 * 2-5 MB, so rendering two dozen of them by default would pull 50-120 MB on the
 * phone of someone whose upload has just been interrupted, quite possibly on the
 * connection that interrupted it.
 *
 * So the free part is the count and the size label, which is what most people
 * need. The pictures load only when asked for, and `loading="lazy"` keeps the
 * off-screen ones from being fetched even then.
 *
 * SIZE MATTERS
 *
 * Groups carry their own photoSize. The same image ordered at 10x15 and at 13x18
 * is two legitimate line items, not a duplicate — so each rescued group is
 * labelled with its size, and a customer now choosing a different size is not
 * being warned about a clash that does not exist.
 */

export interface RescuedGroup {
  id: string;
  /** Localised size label, e.g. "10 x 15 cm sijaj". */
  sizeLabel: string;
  photos: { id: string; name?: string; url?: string; count?: number }[];
}

interface Props {
  groups: RescuedGroup[];
  onDismiss: () => void;
  onRemoveGroup: (groupId: string) => void;
  removingGroupId?: string | null;
  /** Set when a removal failed, so the escape hatch is never a dead button. */
  removeError?: string | null;
}

/** How many thumbnails to render before summarising the remainder. */
const MAX_THUMBS = 24;

const RescuedUploadNotice: React.FC<Props> = ({
  groups,
  onDismiss,
  onRemoveGroup,
  removingGroupId,
  removeError,
}) => {
  const translate = useAppTranslate();
  const t = (key: string, fallback: string, vars?: Record<string, unknown>) => {
    try {
      const out = vars ? translate(key, vars, fallback) : translate(key, fallback);
      return typeof out === "string" && out && out !== key ? out : fallback;
    } catch {
      return fallback;
    }
  };

  const [showPhotos, setShowPhotos] = useState(false);

  const withPhotos = groups.filter((g) => g.photos.length > 0);
  if (withPhotos.length === 0) return null;

  // Prints, not rows. A photo ordered in duplicate carries count > 1, and the
  // reconcile this notice explains counts the same way -- totalling rows would
  // make the notice say "1 photo" beside a cart that says 2.
  const printCount = (photos: { count?: number }[]) =>
    photos.reduce((sum, p) => sum + (p.count ?? 1), 0);

  const total = withPhotos.reduce((sum, g) => sum + printCount(g.photos), 0);

  return (
    <Alert
      severity="info"
      sx={{ mb: 2, alignItems: "flex-start" }}
      onClose={onDismiss}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t(
          "rescuedUpload.title",
          `We saved ${total} ${total === 1 ? "photo" : "photos"} from an upload that was interrupted.`,
          { count: total },
        )}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {t(
          "rescuedUpload.body",
          "They are already in your cart. Check which ones are here, then add only the photos that are missing.",
        )}
      </Typography>

      {removeError && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {removeError}
        </Alert>
      )}

      {withPhotos.map((group) => (
        <Box key={group.id} sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {group.sizeLabel} · {printCount(group.photos)}
          </Typography>

          {showPhotos && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(56px, 1fr))",
              gap: 0.75,
              mt: 0.75,
            }}
          >
            {group.photos.slice(0, MAX_THUMBS).map((photo) => (
              <Box
                key={photo.id}
                component="img"
                src={photo.url}
                alt={photo.name ?? ""}
                title={photo.name ?? ""}
                loading="lazy"
                sx={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  objectFit: "cover",
                  borderRadius: 1,
                  display: "block",
                  bgcolor: "action.hover",
                }}
              />
            ))}
          </Box>
          )}

          {showPhotos && group.photos.length > MAX_THUMBS && (
            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
              {t(
                "rescuedUpload.andMore",
                `+ ${group.photos.length - MAX_THUMBS} more`,
                { count: group.photos.length - MAX_THUMBS },
              )}
            </Typography>
          )}

          {/* The honest escape hatch. If they cannot work out which of their
              own photos are missing, re-uploading everything would double the
              rescued ones -- so let them clear this group and start clean.
              That is the pre-chunking outcome, but chosen rather than imposed. */}
          {!showPhotos && (
            <Button
              size="small"
              color="inherit"
              onClick={() => setShowPhotos(true)}
              sx={{ mt: 0.5, mr: 1, textTransform: "none" }}
            >
              {t("rescuedUpload.showPhotos", "Show me which photos")}
            </Button>
          )}
          <Button
            size="small"
            color="inherit"
            disabled={removingGroupId === group.id}
            onClick={() => onRemoveGroup(group.id)}
            sx={{ mt: 0.5, textTransform: "none" }}
          >
            {removingGroupId === group.id
              ? t("rescuedUpload.removing", "Removing…")
              : t("rescuedUpload.remove", "Remove these and start fresh")}
          </Button>
        </Box>
      ))}
    </Alert>
  );
};

export default RescuedUploadNotice;
